import { GeometryPoint } from '@InstanceFactory';
import 'pepjs';
import { properties } from '../Core/Properties';
import '../Plugins/pointer';
import { enumerable } from '@Common/Core/CoreDecorator';

export interface ICoordinates {
    x: number;
    y: number;
}

enum PointerType {
    mouse,
    touch,
    pen
}

export default class pointerHelper {
    constructor() {
        throw new Error('Cannot create new instance of static class');
    }

    @enumerable
    static get pointerType() {
        return PointerType;
    }

    @enumerable
    static get pointerDownEventName(): string {
        return 'pointerdown';
    }

    @enumerable
    static get pointerUpEventName(): string {
        return 'pointerup';
    }

    @enumerable
    static get pointerMoveEventName(): string {
        return 'pointermove';
    }

    @enumerable
    static get pointerCancelEventName(): string {
        return 'pointercancel';
    }

    @enumerable
    static get pointerOverEventName(): string {
        return 'pointerover';
    }

    @enumerable
    static get pointerOutEventName(): string {
        return 'pointerout';
    }

    @enumerable
    static get pointerEnterEventName(): string {
        return 'pointerenter';
    }

    @enumerable
    static get pointerLeaveEventName(): string {
        return 'pointerleave';
    }

    @enumerable
    static get hasTouch(): boolean {
        const hasTouch =
            'ontouchstart' in window ||
            navigator.maxTouchPoints > 0 ||
            (matchMedia && matchMedia('(any-pointer: coarse)').matches) ||
            navigator.userAgent.toLowerCase().indexOf('touch') > -1;

        return hasTouch;
    }

    @enumerable
    static get needTouchEvents(): boolean {
        return pointerHelper.hasTouch && (properties.isIPad || properties.isIPhone || properties.isIPod || properties.isAndroid);
    }

    @enumerable
    static get hasTouchAndMouse(): boolean {
        return pointerHelper.hasTouch && !pointerHelper.needTouchEvents;
    }

    @enumerable
    static get hasMouse(): boolean {
        return !pointerHelper.hasTouch || pointerHelper.hasTouchAndMouse;
    }

    public static getPointerType(e): PointerType {
        // When we trigger some pointer event, it does not have a specified pointer type
        if (e.isTrigger) {
            return PointerType.mouse;
        }
        return PointerType[(e.originalEvent ? e.originalEvent.pointerType : e.pointerType) as string];
    }

    public static isPrimaryPointerButton(e): boolean {
        if (e.originalEvent && e.originalEvent.button != null) {
            return e.originalEvent.button == 0;
        } else if (e.which != null) {
            return e.which == 1;
        }

        return false;
    }

    public static isSecondaryPointerButton(e): boolean {
        if (e.originalEvent && e.originalEvent.button != null) {
            return e.originalEvent.button == 2;
        } else if (e.which != null) {
            return e.which == 3;
        }

        return false;
    }

    public static getPosition(e, element: JQuery = undefined): ICoordinates {
        let coordinates;

        if (e.originalEvent && e.originalEvent.touches && e.originalEvent.touches.length > 0) {
            coordinates = pointerHelper.getPositionPageRelative(e.originalEvent.touches[0]);
        } else if (e.originalEvent && e.originalEvent.changedTouches && e.originalEvent.changedTouches.length > 0) {
            coordinates = pointerHelper.getPositionPageRelative(e.originalEvent.changedTouches[0]);
        } else if (e.originalEvent) {
            coordinates = pointerHelper.getPositionPageRelative(e.originalEvent);
        } else {
            coordinates = pointerHelper.getPositionPageRelative(e);
        }

        if (element) {
            const offset = element.offset();
            coordinates.x -= offset.left;
            coordinates.y -= offset.top;
        }

        return coordinates;
    }

    public static getPositionPageRelative(eventObject: MouseEvent): { x: number; y: number } {
        const coordinates = {
            x: eventObject.pageX,
            y: eventObject.pageY
        };

        if (coordinates.x === undefined && coordinates.y === undefined) {
            coordinates.x = eventObject.clientX + document.body.scrollLeft + document.documentElement.scrollLeft;
            coordinates.y = eventObject.clientY + document.body.scrollTop + document.documentElement.scrollTop;
        }

        return coordinates;
    }

    public static getElementFromPosition(position: GeometryPoint): Element {
        if (position.x == null || position.y == null) {
            position = pointerHelper.getPosition(position);
        }
        return document.elementFromPoint(position.x, position.y);
    }

    public static setPointerEventsState(state: boolean): void {
        $(document.body).attr('touch-action', state ? 'none' : null);

        if (state) {
            let pointerDownEvent;

            $(document.documentElement).on(pointerHelper.pointerDownEventName, e => {
                pointerDownEvent = e;
            });

            document.documentElement.addEventListener(
                'click',
                clickEvent => {
                    if (pointerDownEvent && (pointerDownEvent.target != clickEvent.target || pointerDownEvent.isClickPrevented())) {
                        clickEvent.stopPropagation();
                    }
                    pointerDownEvent = null;
                },
                true
            );
        }
    }

    public static getAutoScrollCursor(
        leftBoundExceeded: boolean,
        rightBoundExceeded: boolean,
        topBoundExceeded: boolean,
        bottomBoundExceeded: boolean
    ): string {
        let autoScrollCursor = 'auto';

        if (leftBoundExceeded && topBoundExceeded) {
            autoScrollCursor = 'nw-scroll-cursor';
        } else if (rightBoundExceeded && topBoundExceeded) {
            autoScrollCursor = 'ne-scroll-cursor';
        } else if (leftBoundExceeded && bottomBoundExceeded) {
            autoScrollCursor = 'sw-scroll-cursor';
        } else if (rightBoundExceeded && bottomBoundExceeded) {
            autoScrollCursor = 'se-scroll-cursor';
        } else if (leftBoundExceeded) {
            autoScrollCursor = 'w-scroll-cursor';
        } else if (rightBoundExceeded) {
            autoScrollCursor = 'e-scroll-cursor';
        } else if (topBoundExceeded) {
            autoScrollCursor = 'n-scroll-cursor';
        } else if (bottomBoundExceeded) {
            autoScrollCursor = 's-scroll-cursor';
        }

        return autoScrollCursor;
    }
}

export class hoverHelper {
    public static isEnter(e): boolean {
        const type = pointerHelper.getPointerType(e);
        if (type == pointerHelper.pointerType.mouse || type == pointerHelper.pointerType.pen) {
            return !$.contains(e.currentTarget, e.relatedTarget);
        }
        return false;
    }

    public static isLeave(e): boolean {
        const type = pointerHelper.getPointerType(e);
        if (type == pointerHelper.pointerType.mouse || type == pointerHelper.pointerType.pen) {
            return !$.contains(e.currentTarget, e.relatedTarget) && e.currentTarget != e.relatedTarget;
        }
        return false;
    }
}
